import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Separator } from '@/components/ui/separator'
import type {
	Contract,
	CreateContractBody,
	UpdateContractBody,
} from '@/types/contracts'
import type { OrgBranch } from '@/types/organization'
import { CRMultiSelector } from './cr-multi-selector'

export interface ContractFormData {
	name: string
	branchId: string
	crIds: string[]
}

export interface ContractFormProps {
	mode: 'create' | 'edit'
	initial?: Contract
	branches: OrgBranch[]
	usedCrIds: Set<string>
	onSave: (data: CreateContractBody | UpdateContractBody) => void
	onClose: () => void
}

export function ContractFormContent({
	mode,
	initial,
	branches,
	usedCrIds,
	onSave,
	onClose,
}: ContractFormProps) {
	const { t } = useTranslation()

	const [name, setName] = useState(initial?.name ?? '')
	const [branchId, setBranchId] = useState(initial?.branchId ?? '')
	const [crIds, setCrIds] = useState<string[]>(
		initial?.crs.map((cr) => cr.id) ?? [],
	)

	// Reset CRs when branch changes (only for create mode)

	const selectedBranch = branches.find((b) => b.id === branchId)
	const availableCRs = selectedBranch?.crs ?? []

	const handleSave = () => {
		if (!name.trim()) {
			toast.error(t('administration.contracts.form.emptyNameError'))
			return
		}
		if (!branchId) {
			toast.error(t('administration.contracts.form.emptyBranchError'))
			return
		}
		onSave({ name: name.trim(), branchId, crIds })
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			{/* Contract Name */}
			<Field>
				<FieldLabel htmlFor="contract-name">
					{t('administration.contracts.form.nameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="contract-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t('administration.contracts.form.namePlaceholder')}
						maxLength={100}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			{/* Branch Selection */}
			<Field>
				<FieldLabel htmlFor="contract-branch">
					{t('administration.contracts.form.selectBranch')}
				</FieldLabel>
				<FieldContent>
					<NativeSelect
						id="contract-branch"
						value={branchId}
						onChange={(e) => {
							setBranchId(e.target.value)
							if (mode === 'create') setCrIds([])
						}}
					>
						<NativeSelectOption value="" disabled>
							{t('administration.contracts.form.selectBranchPlaceholder')}
						</NativeSelectOption>
						{branches.map((branch) => (
							<NativeSelectOption key={branch.id} value={branch.id}>
								{branch.name}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</FieldContent>
			</Field>

			{/* CR Multi-selector (only shown after branch is selected) */}
			{branchId && (
				<>
					<Separator className="bg-border/60" />
					<CRMultiSelector
						crs={availableCRs}
						selected={crIds}
						onChange={setCrIds}
						usedCrIds={usedCrIds}
						currentContractId={initial?.id}
					/>
				</>
			)}

			<Separator className="bg-border/60" />

			{/* Actions */}
			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.contracts.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create'
						? t('administration.contracts.form.saveButton')
						: t('administration.contracts.form.saveEditButton')}
				</Button>
			</div>
		</div>
	)
}
